import type qs from 'qs';
import { useCallback } from 'react';

import type { NavigateOptions } from '../use-navigate';
import { useBetterNavigate } from '../use-navigate';

import type { Lens } from './lens';
import { parseQueryStringLens } from './parse-query-string-lens';

type SetUrlQuery<A, B> = (
	value: B | ((a: A) => B),
	options?: NavigateOptions
) => void;

/**
 * Returns a setter function just like `useUrlQueryLens`,
 * but does not cause rerenders when the value changes.
 */
export function useSetUrlQueryLens<A, B>(
	lens: Lens<qs.ParsedQs, unknown, A, B>,
	navigateOptions?: NavigateOptions
) {
	const navigate = useBetterNavigate(navigateOptions);

	return useCallback<SetUrlQuery<A, B>>(
		(value, options) => {
			const l = parseQueryStringLens.then(lens);

			navigate(
				({ search }) => ({
					search: l.set(
						typeof value === 'function'
							? (value as (a: A) => B)(l.get(search))
							: value,
						search
					),
				}),
				options
			);
		},
		[lens, navigate]
	);
}
